import type { ReactNode } from 'react';

/**
 * TTPill / .pill — status badge. Metrics from TTDesignKit: 11px/800, tracking .33,
 * padding 11×5, full capsule.
 *
 * Status is encoded by shape and text, never by colour alone (docs/22 §3.1): the pill
 * always carries a word, so it survives colour blindness and a greyscale screenshot.
 */
type Variant = 'success' | 'warning' | 'info' | 'danger' | 'neutral';

const VARIANTS: Record<Variant, string> = {
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  info: 'bg-info-soft text-info',
  danger: 'bg-accent-soft text-accent-soft-ink',
  // `.pill-neutral` sits on `surface-alt`, one step lighter than `surface-alt-2`, so it does not
  // read as a filled control standing next to the coloured variants.
  neutral: 'bg-surface-alt text-ink-soft',
};

/**
 * ПОЧЕМУ ПЛАШКА УМЕЕТ СЖИМАТЬСЯ.
 *
 * Плашка почти всегда стоит в одном ряду с текстом, которому тоже не хватает места: название
 * техники на главной, сумма и дата в истории, адрес в карточке неоплаченного СБП. Раньше она
 * забирала свою max-content ширину целиком и не отдавала ни пикселя (`shrink-0`, а где его не
 * было — `min-width: auto` у flex-элемента, что то же самое). На телефоне 360 CSS-px «Ищем
 * оператора» — это 128 px из 292 px строки: названию оставалось 98 px из нужных 182, и человек
 * видел «Mercedes-Ben…», а на 320 px «Merc…». В истории при той же причине не помещалась дата
 * заказа — ни на одном телефоне.
 *
 * Поэтому здесь `min-w-0` + `truncate` и НЕТ ни `shrink-0`, ни потолка ширины. Нехватку ширины
 * flexbox делит между плашкой и текстом, а значит:
 *
 * - на колонке 560 px (планшет, десктоп) нехватки нет вовсе, и плашка остаётся целой — жёсткий
 *   потолок вроде `max-w-[14ch]` резал бы «Ищем оператора» и там, где места вдоволь;
 * - короткая метка («Отмена», «В пути», «Истёк») не занимает лишнего: пола ширины тоже нет, а
 *   `min-width` пола добавил бы «Истёку» сорок пикселей пустоты;
 * - на узком телефоне обе стороны отдают, и ни одна не пропадает целиком.
 *
 * `shrink-[0.5]` — вес этого дележа, и он подобран по замерам, а не на глаз. При равном весе
 * (`shrink-1`) на 360 CSS-px плашка отдавала 44 px из 128 и превращалась в «Нужна …» — статус, по
 * которому уже ничего не понять, хотя ради него строка и существует. С половинным весом она отдаёт
 * 27 px («Нужна доп…», «Ищем опер…»), а остальное забирает текст, и в истории этого хватает, чтобы
 * и сумма, и дата заказа поместились целиком.
 *
 * Полный текст всегда остаётся в `title`.
 *
 * ЧТОБЫ ЭТО РАБОТАЛО, соседняя текстовая колонка должна быть `flex-auto`, а не `flex-1`: у `flex-1`
 * база 0, ряд тогда не переполняется никогда, и плашку просто нечем прижать. Так сделано в
 * ActiveOrderCard, PendingSbpPayments и HistoryTab — в трёх рядах, где плашка мешала соседу.
 */
export function Pill({
  variant = 'neutral',
  children,
  title,
  live = false,
}: {
  variant?: Variant;
  children: ReactNode;
  /** Полный текст для подсказки, когда `children` — не строка и вывести его неоткуда. */
  title?: string;
  /**
   * Метка меняется под опросом, пока человек смотрит на экран, — её надо объявлять.
   *
   * Область живёт на самой плашке, а не на обёртке вокруг: обёртка была бы ещё одним flex-элементом
   * между рядом и плашкой, и весь расчёт ширины выше пришлось бы дублировать на ней.
   */
  live?: boolean;
}) {
  return (
    <span
      aria-live={live ? 'polite' : undefined}
      title={title ?? (typeof children === 'string' ? children : undefined)}
      className={
        // `max-w-full` — на случай, если плашку всё же завернут в свой контейнер.
        'inline-block max-w-full min-w-0 shrink-[0.5] truncate rounded-full px-[11px] py-[5px] ' +
        // Межстрочие относительное, а не жёсткие 14px: при системном увеличении шрифта
        // («Масштабирование текста» в Android, «Минимальный размер шрифта» в Chrome) буквы
        // вырастают, а px-строка — нет, и текст выпирает из капсулы.
        `text-[11px] font-extrabold leading-[1.27] tracking-[0.033em] ${VARIANTS[variant]}`
      }
    >
      {children}
    </span>
  );
}
